/**
 * Whether a company-based list's Find person may hand back people the workspace already holds
 * (PREC-3899). Stored per list in `list.findPersonContactMode` (a STRING column, so a new value
 * needs no schema change; the values are pinned by a spec).
 *
 * Not to be confused with {@link FindPersonMode} (PREC-4400), which decides *when* people are
 * searched (now, or later from the Companies tab). This one decides *whom* a search may return.
 */
export enum FindPersonContactMode {
	/**
	 * Today's behaviour and the default: the company's own prospects at the organisation are reused
	 * first (free unless their data is older than 30 days), and Company Enrich fills the slots left.
	 */
	CONTACTS_FIRST = "CONTACTS_FIRST",
	/**
	 * Nobody already in the workspace's Contacts is returned. The reuse step does not run, and
	 * Company Enrich's free preview is screened against the contacts at the company before any
	 * paid call, so only new people fill the slots.
	 */
	NEW_PEOPLE_ONLY = "NEW_PEOPLE_ONLY",
}

export const DEFAULT_FIND_PERSON_CONTACT_MODE = FindPersonContactMode.CONTACTS_FIRST;

/** What the decision reads of a list. Raw strings: the enums are separate on the API and the web. */
export interface FindPersonWaterfallList {
	sourceType?: string | null;
	entityType?: string | null;
	scrapeLevel?: string | null;
}

/** Source types whose company rows are searched with the find-person waterfall when they are company lists. */
const COMPANY_UPLOAD_SOURCE_TYPES = ["CSV_ENRICHMENTS", "CUSTOM_EVENTS"];

/** Source types that search people at their companies only at the GET_ENRICHMENT scrape level. */
const ENRICHMENT_LEVEL_SOURCE_TYPES = ["MAPS", "JOBS", "PARALLEL_AI"];

/**
 * Whether the list finds people at its companies with the find-person waterfall (our own
 * prospects, then Company Enrich, then AI research): company uploads, Maps, Jobs, Funding/M&A and
 * Parallel AI, exactly where the list builder shows the search profiles. On these lists the
 * {@link FindPersonContactMode} replaces "Only new leads"; every other list type keeps it.
 *
 * The one place this is decided, so the builder's "which control do I show?" and the scrapers'
 * "which setting do I read?" cannot drift (a control does what it names, PREC-4129).
 */
export function usesFindPersonWaterfall(list: FindPersonWaterfallList): boolean {
	const sourceType = list.sourceType ?? "";

	if (COMPANY_UPLOAD_SOURCE_TYPES.includes(sourceType)) {
		return list.entityType === "ORGANIZATION";
	}

	if (ENRICHMENT_LEVEL_SOURCE_TYPES.includes(sourceType)) {
		return list.scrapeLevel === "GET_ENRICHMENT";
	}

	return sourceType === "FUNDING_ROUNDS";
}

/** The list fields the two readers below take. */
export interface FindPersonContactModeList extends FindPersonWaterfallList {
	findPersonContactMode?: string | null;
	onlyNewLeads?: boolean | null;
}

/**
 * The mode a list's Find person runs with. Only a waterfall list can be in "Find new people
 * only"; anything unreadable, and every other list type, reads as the default.
 */
export function findPersonContactModeOf(list: FindPersonContactModeList): FindPersonContactMode {
	return usesFindPersonWaterfall(list) &&
		list.findPersonContactMode === FindPersonContactMode.NEW_PEOPLE_ONLY
		? FindPersonContactMode.NEW_PEOPLE_ONLY
		: FindPersonContactMode.CONTACTS_FIRST;
}

/**
 * Whether the list refuses a lead that is already on another of the company's lists (the
 * `ListDuplicateGuardService` check). On a waterfall list the mode replaced the "Only new leads"
 * toggle, so the stored `onlyNewLeads` is not read there: "Find new people only" refuses, "Use my
 * contacts first" does not. Every other list type reads `onlyNewLeads` as before.
 */
export function refusesLeadsOnOtherLists(list: FindPersonContactModeList): boolean {
	if (usesFindPersonWaterfall(list)) {
		return findPersonContactModeOf(list) === FindPersonContactMode.NEW_PEOPLE_ONLY;
	}

	return !!list.onlyNewLeads;
}

/** What a save may change of the fields the rule below reads. */
export interface LeadNoveltyChange extends FindPersonWaterfallList {
	onlyNewLeads?: boolean | null;
	findPersonContactMode?: string | null;
}

/**
 * "Only new leads" and the Find person mode are one intent stored twice (PREC-3899): every save
 * writes both, so a list that changes type keeps the choice it had (a Maps list with "Only new
 * leads" that moves to the enrichment level reads "Find new people only", and back), and a worker
 * of the release before, or a migration undo, reads the closest intent from `onlyNewLeads`.
 *
 * The setting the list's type (after the save) reads is the one that decides: the mode on a list
 * that finds people at its companies, `onlyNewLeads` everywhere else; a save that names only the
 * other one is taken as given. Answers the fields to store, or nothing when the save names neither.
 */
export function leadNoveltyFields(
	list: FindPersonWaterfallList,
	change: LeadNoveltyChange
): { onlyNewLeads: boolean; findPersonContactMode: FindPersonContactMode } | Record<string, never> {
	const typeAfter = {
		sourceType: change.sourceType ?? list.sourceType,
		entityType: change.entityType ?? list.entityType,
		scrapeLevel: change.scrapeLevel ?? list.scrapeLevel,
	};
	const modeGiven = change.findPersonContactMode != null;
	const onlyNewLeadsGiven = change.onlyNewLeads != null;
	const modeDecides = modeGiven && (usesFindPersonWaterfall(typeAfter) || !onlyNewLeadsGiven);

	if (modeDecides) {
		const newOnly = change.findPersonContactMode === FindPersonContactMode.NEW_PEOPLE_ONLY;

		return {
			onlyNewLeads: newOnly,
			findPersonContactMode: newOnly
				? FindPersonContactMode.NEW_PEOPLE_ONLY
				: FindPersonContactMode.CONTACTS_FIRST,
		};
	}

	if (onlyNewLeadsGiven) {
		return {
			onlyNewLeads: !!change.onlyNewLeads,
			findPersonContactMode: change.onlyNewLeads
				? FindPersonContactMode.NEW_PEOPLE_ONLY
				: FindPersonContactMode.CONTACTS_FIRST,
		};
	}

	return {};
}
